import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';

type DbxState = { host: string; configured: boolean } | null;

/**
 * Integrations — per-user third-party connections used by demos. Databricks is the first: connect a
 * workspace (host + admin PAT, stored server-side, never returned) so Delta Sharing demos can validate
 * and auto-create the recipient. Credentials are scoped to the signed-in user.
 */
export function Integrations() {
  const [dbx, setDbx] = useState<DbxState>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setDbx(await api.databricksConfig());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <main className="content">
      <h1>Integrations</h1>
      <p className="muted">
        Connect third-party services your demos depend on. Connections are scoped to your account.
      </p>

      {error && <p className="error">{error}</p>}

      <div className="integrations">
        <DatabricksCard dbx={loading ? undefined : dbx} onChanged={load} />
      </div>
    </main>
  );
}

const STATUS = {
  connected: { pill: 'ok', label: 'Connected' },
  partial: { pill: 'warn', label: 'Host set · no PAT' },
  off: { pill: 'off', label: 'Not connected' },
} as const;

function DatabricksCard({ dbx, onChanged }: { dbx: DbxState | undefined; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [host, setHost] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => setHost(dbx?.host ?? ''), [dbx?.host]);

  const state = dbx?.configured ? STATUS.connected : dbx?.host ? STATUS.partial : STATUS.off;

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.setDatabricksConfig(host.trim(), token.trim());
      setToken('');
      setOpen(false);
      onChanged();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'save failed';
      setErr(
        msg === 'databricks_auth_failed'
          ? 'Databricks rejected the token — check the PAT + host.'
          : msg === 'invalid_host'
            ? 'Host must be an https:// workspace URL.'
            : msg,
      );
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.clearDatabricksConfig();
      setToken('');
      setOpen(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="integration-card">
      <div className="integration-head">
        <div className="integration-title">
          <span className="integration-name">Databricks</span>
          <span className="muted small-note">Unity Catalog + Delta Sharing consumer for Tables demos</span>
        </div>
        {dbx === undefined ? (
          <span className="pill off">…</span>
        ) : (
          <span className={`pill ${state.pill}`}>{state.label}</span>
        )}
      </div>

      {dbx?.host && <div className="integration-detail">{dbx.host.replace(/^https:\/\//, '')}</div>}

      {open ? (
        <div className="dbx-form">
          <label className="field">
            <span>Workspace host</span>
            <input
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="https://dbc-....cloud.databricks.com"
            />
          </label>
          <label className="field">
            <span>Personal access token (admin; stored as a secret, never shown again)</span>
            <input value={token} onChange={(e) => setToken(e.target.value)} placeholder="dapi…" type="password" />
          </label>
          {err && <p className="error">{err}</p>}
          <div className="actions">
            <button
              type="button"
              className="btn primary"
              disabled={busy || !host.trim() || !token.trim()}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save + verify'}
            </button>
            <button type="button" className="btn ghost" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="actions">
          <button type="button" className="btn primary" disabled={dbx === undefined} onClick={() => setOpen(true)}>
            {dbx?.host ? 'Reconfigure' : 'Connect Databricks'}
          </button>
          {dbx?.host && (
            <button type="button" className="btn ghost danger" disabled={busy} onClick={() => void disconnect()}>
              {busy ? 'Disconnecting…' : 'Disconnect'}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
